// `OnboardingShell` — full-screen 6-step wizard overlay (US-016, US-049).
//
// Mounts inside the authenticated route shell when the current user has
// `onboardingCompletedAt = null`. Renders the active step + the
// 6-pill progress indicator. Server-side persistence runs through the
// `useWizard()` state machine.

import { useCallback, useState } from 'react';

import {
  useCompleteOnboarding,
  useLinkedinAccounts,
  useUpdateLinkedinAccount,
  type OnboardingProgress,
  type StartTrialResponse,
} from '@barel/api-client/react';
import { useT } from '@barel/i18n';
import { BrandLockup, OnboardingStepIndicator, Spinner } from '@barel/ui';

import { runMutationWithToast } from '@/lib/mutationToast';
import { useNavigate } from '@tanstack/react-router';

import type { OnboardingGoal, DefaultInboxMode } from '@barel/core';

import { useWizard } from './lib/useWizard';
import type { OnboardingPlan } from './lib/schemas';
import { Welcome } from './steps/Welcome';
import { Goal } from './steps/Goal';
import { Connect } from './steps/Connect';
import { AIDemo } from './steps/AIDemo';
import { Plan } from './steps/Plan';
import { Payment } from './steps/Payment';

export interface OnboardingShellProps {
  /** Disable network round-trips (tests / Storybook). */
  enabled?: boolean;
  /**
   * Optional initial state — used by tests + stories so the wizard can mount
   * mid-flow without round-tripping the server.
   */
  initialProgress?: OnboardingProgress;
  /** Override goal-resolution route; defaults to the production paths below. */
  onComplete?: (goal: OnboardingGoal) => void;
}

// US-029 — goal-driven landing.
//   - inbox  → `/inbox`         (review-on-all default)
//   - campaigns → `/campaigns`  (review-on-all default — campaigns goal still
//     wants to receive replies via the inbox so we set review)
//   - both   → `/inbox`         (the user wants both surfaces; inbox is the
//     primary daily landing per spec)
const GOAL_TO_PATH: Record<OnboardingGoal, string> = {
  inbox: '/inbox',
  campaigns: '/campaigns',
  both: '/inbox',
};

export function OnboardingShell({
  enabled = true,
  initialProgress,
  onComplete,
}: OnboardingShellProps) {
  const t = useT('onboarding');
  const navigate = useNavigate();
  const completeMutation = useCompleteOnboarding();
  const updateLinkedinAccount = useUpdateLinkedinAccount();
  // List paired accounts so we can seed `aiMode` per goal on complete
  // (US-029). Cheap enough to keep enabled while the wizard is open since the
  // route-tree gates the wizard to authenticated users only.
  const linkedinAccounts = useLinkedinAccounts({ enabled });
  const wizard = useWizard({ enabled });

  // Allow tests to seed an initial state without going through the network.
  useState(() => {
    if (initialProgress) wizard.hydrate(initialProgress);
  });

  const onPaymentSubmit = useCallback(
    // Reached ONLY after `POST /billing/start-trial` succeeded inside the
    // Payment step (US-029 — "On Stripe success, onComplete runs"). The
    // subscription row exists server-side before completion is recorded; a
    // trial failure never reaches this handler, keeping the user on step 6.
    async (trialResult: StartTrialResponse) => {
      void trialResult;
      const goal = wizard.state.goal ?? 'both';
      await runMutationWithToast(
        async () => {
          await completeMutation.mutateAsync({
            goal: wizard.state.goal,
            inboxMode: wizard.state.inboxMode,
            plan: wizard.state.plan,
          });
          // US-029 — seed per-account `aiMode` based on the user's goal.
          //   - inbox / both:   honour `inboxMode` (auto | review)
          //   - campaigns:      `review` (campaigns-goal users still want to
          //                     review every reply that lands in the inbox)
          // Best-effort: a failed update MUST NOT block the onboarding flow.
          const accountAiMode: 'auto' | 'review' =
            goal === 'campaigns' ? 'review' : (wizard.state.inboxMode ?? 'review');
          const accounts = linkedinAccounts.data?.accounts ?? [];
          await Promise.allSettled(
            accounts.map((acc) =>
              updateLinkedinAccount.mutateAsync({
                id: acc.id,
                settings: { aiMode: accountAiMode },
              }),
            ),
          );
        },
        {
          success: { title: t('complete.toastTitle'), description: t('complete.toastBody') },
          onSuccess: () => {
            if (onComplete) {
              onComplete(goal);
            } else {
              void navigate({ to: GOAL_TO_PATH[goal], replace: true });
            }
          },
          error: { title: t('complete.errorTitle'), description: t('complete.errorBody') },
        },
      );
    },
    [
      completeMutation,
      linkedinAccounts.data,
      navigate,
      onComplete,
      t,
      updateLinkedinAccount,
      wizard.state.goal,
      wizard.state.inboxMode,
      wizard.state.plan,
    ],
  );

  if (wizard.isLoading) {
    return (
      <main
        id="main-content"
        tabIndex={-1}
        data-region="onboarding-shell"
        data-state="loading"
        className="ls-focus-ring flex min-h-[100vh] items-center justify-center bg-bg-base outline-none"
      >
        <Spinner size="lg" tone="accent" aria-label={t('wizard.loading')} />
      </main>
    );
  }

  return (
    <main
      id="main-content"
      tabIndex={-1}
      data-region="onboarding-shell"
      data-step={wizard.step}
      className="ls-focus-ring relative flex min-h-[100vh] w-full flex-col items-center justify-start gap-6 overflow-y-auto overflow-x-hidden bg-bg-base px-5 py-10 outline-none"
      aria-labelledby="onboarding-heading"
    >
      <h2 id="onboarding-heading" className="sr-only">
        {t('wizard.title')}
      </h2>

      {/* Ambient gradient + dot grid (decorative). */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0"
        style={{
          backgroundImage:
            'radial-gradient(ellipse at 28% 38%, rgba(91,110,248,0.13) 0%, transparent 55%), radial-gradient(ellipse at 72% 68%, rgba(52,211,153,0.07) 0%, transparent 50%)',
        }}
      />
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-10"
        style={{
          backgroundImage: 'radial-gradient(circle, rgba(255,255,255,0.55) 1px, transparent 1px)',
          backgroundSize: '30px 30px',
        }}
      />

      <div className="z-10 mt-auto">
        <BrandLockup />
      </div>

      <div className="z-10">
        <OnboardingStepIndicator
          current={wizard.step}
          total={6}
          aria-label={t('wizard.progressLabel')}
          aria-valuetext={t('wizard.stepOf', { current: wizard.step, total: 6 })}
        />
      </div>

      <div className="z-10 flex w-full justify-center">
        {wizard.step === 1 ? (
          <Welcome onContinue={() => wizard.goto(2)} />
        ) : wizard.step === 2 ? (
          <Goal
            goal={wizard.state.goal ?? null}
            inboxMode={wizard.state.inboxMode ?? null}
            onGoalChange={(goal: OnboardingGoal) => {
              const update: Partial<OnboardingProgress> = { goal };
              if (goal === 'campaigns') update.inboxMode = undefined;
              wizard.patch(update);
            }}
            onInboxModeChange={(mode: DefaultInboxMode) => wizard.patch({ inboxMode: mode })}
            onBack={() => wizard.goto(1)}
            onContinue={() => wizard.goto(3)}
          />
        ) : wizard.step === 3 ? (
          <Connect
            method={wizard.state.connectMethod ?? 'extension'}
            connected={!!wizard.state.connected}
            onMethodChange={(m) => wizard.patch({ connectMethod: m })}
            onConnected={() => wizard.patch({ connected: true })}
            onDisconnected={() => wizard.patch({ connected: false })}
            onBack={() => wizard.goto(2)}
            onContinue={() => wizard.goto(4)}
          />
        ) : wizard.step === 4 ? (
          <AIDemo
            goal={wizard.state.goal ?? null}
            inboxMode={wizard.state.inboxMode ?? null}
            onBack={() => wizard.goto(3)}
            onContinue={() => wizard.goto(5)}
          />
        ) : wizard.step === 5 ? (
          <Plan
            plan={(wizard.state.plan ?? null) as OnboardingPlan | null}
            onPlanChange={(p) => wizard.patch({ plan: p })}
            onBack={() => wizard.goto(4)}
            onContinue={() => wizard.goto(6)}
          />
        ) : (
          <Payment
            plan={(wizard.state.plan ?? 'scale') as OnboardingPlan}
            submitting={completeMutation.isPending}
            onBack={() => wizard.goto(5)}
            onSubmit={onPaymentSubmit}
          />
        )}
      </div>

      <div className="z-10 mb-auto text-2xs text-tertiary">
        {t('wizard.help')}{' '}
        <a href="mailto:hello@barel.app" className="text-secondary underline">
          {t('wizard.helpLink')}
        </a>
      </div>
    </main>
  );
}
